import type { ReactNode } from "react";

/**
 * The design's layout, present but unreadable, behind a "Coming soon" label.
 *
 * WHY BLUR RATHER THAN AN EMPTY PANEL. Two things were both true and pulled
 * against each other: an operator must never mistake an invented number for a
 * real one, and a console that simply omits half the design tells them nothing
 * about what it will become. Blurring resolves it — the shape of the panel is
 * visible, the figures are physically unreadable, and the label says why. You
 * can see that a cost chart belongs here without being able to read a cost.
 *
 * So the placeholder inside this MUST be shape only. It is `aria-hidden`, so a
 * screen reader never meets it, and `select-none` so it cannot be copied out of
 * the page and pasted somewhere it would look authoritative.
 *
 * The label is not decoration: `needs` names the API that has to exist, which
 * is the actionable half. "Coming soon" alone would leave the reader guessing
 * whether it is a week away or a different team's roadmap.
 */
export function Blurred({
  what,
  needs,
  children,
  minHeight,
}: {
  /** What this panel will show. Reads as a title on the overlay. */
  what: string;
  /** The API or system it is waiting on. */
  needs: string;
  /** Shape-only placeholder. Never real data, never readable. */
  children: ReactNode;
  minHeight?: number;
}) {
  return (
    // `h-full` on both the frame and the shape inside it. In a grid row the
    // taller sibling stretches this cell, and without it the placeholder card
    // stayed at its own height while the frame grew — leaving the "coming soon"
    // label floating in a tall empty box below a panel that stopped early.
    <div className="relative isolate h-full overflow-hidden rounded-lg" style={{ minHeight }}>
      <div
        aria-hidden="true"
        className="pointer-events-none h-full select-none blur-[6px] saturate-50 opacity-45 *:h-full"
      >
        {children}
      </div>

      <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 p-4 text-center">
        <span className="rounded-full bg-slate-950/85 px-2.5 py-1 text-[10px] font-semibold tracking-wider text-white uppercase dark:bg-white/90 dark:text-slate-950">
          Coming soon
        </span>
        <p className="text-sm/5 font-medium text-slate-950 dark:text-white">{what}</p>
        <p className="max-w-xs text-xs/5 text-slate-600 dark:text-slate-300">Needs {needs}</p>
      </div>
    </div>
  );
}
